import type { SavedViewItem } from "@/data-access/saved-views/queries/saved-views-queries";
import type { StateCreator } from "zustand";
import { create } from "zustand";
import { type DevtoolsOptions, devtools } from "zustand/middleware";
import { createSavedViewActions } from "./saved-views-management.actions";

export type SavedViewModalStateSlice = {
  isCreateOrEditSavedViewModalOpen: boolean;
  isDeleteSavedViewModalOpen: boolean;
  setIsCreateOrEditSavedViewModalOpen: (isOpen: boolean) => void;
  setIsDeleteSavedViewModalOpen: (isOpen: boolean) => void;
};

export type SavedViewCreateEditStateSlice = {
  selectedSavedViewId: string | null;
  name: string;
  isCreatingSavedView: boolean;
  isUpdatingSavedView: boolean;
  createEditSavedViewState: "initial" | "complete";
  setSelectedSavedViewId: (id: string | null) => void;
  setSavedViewName: (name: string) => void;
  onEditSavedView: (params: { savedViewId: string; name: string }) => void;
  resetCreateEditSavedView: () => void;
};

export type SavedViewDeleteStateSlice = {
  isDeletingSavedView: boolean;
  resetDeleteSavedView: () => void;
};

export type SavedViewDataFetchSlice = {
  isFetchingSavedViews: boolean;
  allSavedViews: Map<SavedViewItem["id"], SavedViewItem>;
};

export type SavedViewStateSlice = SavedViewModalStateSlice &
  SavedViewCreateEditStateSlice &
  SavedViewDeleteStateSlice &
  SavedViewDataFetchSlice;

export const createEditSavedViewInitialState = {
  selectedSavedViewId: null,
  name: "",
  isCreatingSavedView: false,
  isUpdatingSavedView: false,
  createEditSavedViewState: "initial" as const,
  isCreateOrEditSavedViewModalOpen: false,
};

export const deleteSavedViewInitialState = {
  isDeletingSavedView: false,
  isDeleteSavedViewModalOpen: false,
};

const savedViewsDataInitialState = {
  isFetchingSavedViews: false,
  allSavedViews: new Map<SavedViewItem["id"], SavedViewItem>(),
};

const createSavedViewSlice: StateCreator<
  SavedViewStateSlice,
  [["zustand/devtools", never]]
> = (set, get, api) => ({
  ...createEditSavedViewInitialState,
  ...deleteSavedViewInitialState,
  ...savedViewsDataInitialState,
  ...createSavedViewActions(set, get, api),
});

const useSavedViewStore = create<SavedViewStateSlice>()(
  devtools(createSavedViewSlice, {
    name: "saved-views-management-store",
    serialize: {
      options: true,
    },
  } as unknown as DevtoolsOptions),
);

export default useSavedViewStore;
